import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Button, FluentProvider, Spinner, useId, webLightTheme,
} from '@fluentui/react-components';
import { Toast, ToastTitle, useToastController } from '@fluentui/react-toast';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { I18nModel } from '@bingads-webui/copilot-i18n';
import { BannerState } from './types';

const COMMON_DISPATCH_TOAST_OPTIONS = {
  position: 'bottom-start',
  timeout: -1,
};

export interface IUseCopilotBannerToastProps {
  i18n: I18nModel;
  isToastEnabled: boolean;
  currentState: BannerState;
}

export const useCopilotBannerToast = ({
  i18n,
  isToastEnabled,
  currentState,
}: IUseCopilotBannerToastProps) => {
  const toasterId = useId('toaster');

  const { dispatchToast, dismissAllToasts } = useToastController(toasterId);

  const CloseButton = useMemo(() => (
    <Button
      className="close-button"
      appearance="subtle"
      icon={<Dismiss24Regular />}
      title={i18n.getString(_TL_('Close'))}
      aria-label={i18n.getString(_TL_('Close'))}
      onClick={dismissAllToasts}
    />
  ), [i18n, dismissAllToasts]);

  const notify = useCallback((state: BannerState) => {
    if (state === 'Applying') {
      dismissAllToasts();

      // because of a bug in this older version of fluent v9, we need to wrap toast in a FluentProvider
      dispatchToast(
        <FluentProvider theme={webLightTheme}>
          <Toast className="applying-toast">
            <ToastTitle media={<Spinner size="tiny" />} action={CloseButton}>
              {i18n.getString(_TL_('We\'re generating new asset recommendations and adding assets from the selected themes to your asset group.'))}
            </ToastTitle>
          </Toast>
        </FluentProvider>,
        COMMON_DISPATCH_TOAST_OPTIONS as any, // @fluentui/react-toast doesn't export the DispatchToastOptions type
      );
    } else if (state === 'ApplyingSucceeded') {
      dismissAllToasts();

      dispatchToast(
        <FluentProvider theme={webLightTheme}>
          <Toast className="applying-succeeded-toast">
            <ToastTitle action={CloseButton}>
              {i18n.getString(_TL_('We\'ve updated the text and image asset recommendations.'))}
            </ToastTitle>
          </Toast>
        </FluentProvider>,
        {
          ...COMMON_DISPATCH_TOAST_OPTIONS,
          intent: 'success',
          timeout: 5000,
        } as any, // @fluentui/react-toast doesn't export the DispatchToastOptions type
      );
    } else if (state === 'ApplyingFailed') {
      dismissAllToasts();
    }
  }, [i18n, dismissAllToasts, dispatchToast, CloseButton]);

  useEffect(() => {
    if (isToastEnabled) {
      notify(currentState);
    }
  }, [isToastEnabled, currentState]);

  return {
    toasterId,
    notify,
  };
};
